/**
 * 存储用户权限列表的会话键名称。
 */
const USER_AUTH_KEY = "USER_AUTH";
/**
 * 存储系统按钮权限的会话键名称。
 * 预留常量便于后续扩展按钮级权限控制。
 */
const SYS_BUTTON_AUTH_KEY = "SYS_BUTTON_AUTH";
/**
 * 存储数据权限列表的会话键名称。
 */
const DATA_AUTH_KEY = "DATA_AUTH";

/**
 * 安全解析权限 JSON 字符串，异常时返回空数组。
 * @param {string|null} raw 原始会话存储字符串。
 * @returns {Array<*>} 合法权限条目数组。
 */
function safeParseAuth(raw) {
  if (!raw) {
    return [];
  }
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
}

/**
 * 抽取权限条目中的动作编码集合。
 * @param {Array<Object>} entries 权限条目列表。
 * @returns {string[]} 规范化的动作编码数组。
 */
function extractActions(entries) {
  if (!Array.isArray(entries)) {
    return [];
  }
  return entries
    .map((item) => (item && typeof item === "object" ? item.action : null))
    .filter((action) => typeof action === "string" && action);
}

/**
 * 获取当前用户的全部权限动作编码列表。
 * @returns {string[]} 去重后的动作编码集合。
 */
export function getUserActions() {
  if (typeof window === "undefined" || !window.sessionStorage) {
    return [];
  }
  const authList = safeParseAuth(window.sessionStorage.getItem(USER_AUTH_KEY));
  const actions = extractActions(authList);
  return Array.from(new Set(actions));
}

/**
 * 判断当前用户是否具备指定动作的访问权限。
 * @param {string} action 需要验证的权限动作标识。
 * @returns {boolean} true 表示用户拥有该权限。
 */
export function hasActionPermission(action) {
  if (!action || typeof action !== "string") {
    return false;
  }
  return getUserActions().includes(action);
}

/**
 * 从会话存储中解析数据权限条目，返回空数组保证调用安全。
 * @returns {Array<Object>} 数据权限条目集合。
 */
function getDataAuthEntries() {
  if (typeof window === "undefined" || !window.sessionStorage) {
    return [];
  }
  return safeParseAuth(window.sessionStorage.getItem(DATA_AUTH_KEY));
}

/**
 * 构建唯一权限编码集合，去除空值与空白字符。
 * @param {Array<string|Object>} entries 权限条目或编码数组。
 * @returns {Set<string>} 去重后的权限编码集合。
 */
function buildUniqueCodeSet(entries) {
  const codes = new Set();
  if (!Array.isArray(entries)) {
    return codes;
  }
  entries.forEach((item) => {
    const value =
      typeof item === "string"
        ? item
        : item && typeof item === "object"
        ? item.itemValue
        : null;
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed) {
        codes.add(trimmed);
      }
    }
  });
  return codes;
}

/**
 * 获取当前用户具备的数据权限编码集合。
 * @param {string} [dictCode] 指定权限字典编码，缺省时返回全部数据权限。
 * @returns {Set<string>} 用户权限编码集合。
 */
export function getUserDataAuthValues(dictCode) {
  const entries = getDataAuthEntries();
  if (dictCode) {
    return buildUniqueCodeSet(
      entries.filter(
        (item) => item && typeof item === "object" && item.dictCode === dictCode
      )
    );
  }
  return buildUniqueCodeSet(entries);
}

/**
 * 判定当前用户是否拥有涉密资料查看权限。
 * @param {string} dictCode 涉密等级对应的字典编码。
 * @returns {boolean} true 表示具备涉密访问权限。
 */
export function hasSecretDataPermission(
  dictCode = "secret_level"
) {
  const userCodes = getUserDataAuthValues(dictCode);
  if (!userCodes.size) {
    return false;
  }
  return true;
}
